import type { LogoName } from '@highlight/ui'
import { useModelChoice } from '@renderer/chat/hooks/use-plan-features'
import { useModels } from '@renderer/chat/store/models'
import type { MentionableModel } from '@renderer/chat/types'

export type SearchModelKey = 'auto' | 'claude' | 'chatgpt' | 'gemini'

interface SearchModelChoice {
  key: Exclude<SearchModelKey, 'auto'>
  label: string
  logo: LogoName
  modelId: string
}

/** The companies the bar offers by name, each pointed at one of its models. Change an id to retarget a row. */
export const SEARCH_MODEL_CHOICES: Array<SearchModelChoice> = [
  { key: 'claude', label: 'Claude', logo: 'claude', modelId: '62fd26c8-5b54-4a6d-a238-1785c50c78d8' }, // Claude 5.5 Sonnet
  { key: 'chatgpt', label: 'ChatGPT', logo: 'openai', modelId: 'f4f9b08f-fbdb-4e74-b5a1-5d816adde77c' }, // GPT-5.6 Sol
  { key: 'gemini', label: 'Gemini', logo: 'gemini', modelId: 'e71339bc-4b71-48d8-8744-b44270a8c686' }, // Gemini 3.1 Pro
]

/** The model a choice sends to: its own, or the one the server now serves in its place once it's deprecated. */
function findChoiceModel(models: Array<MentionableModel>, modelId: string): MentionableModel | undefined {
  return models.find((model) => model.id === modelId) ?? models.find((model) => model.meta?.replaces?.includes(modelId))
}

/**
 * The model behind `key`, or undefined for Auto, while the model list hasn't
 * loaded, and on a plan without model choice, so a pick left from a paid period
 * never reaches a chat.
 */
export function useSearchModel(key: SearchModelKey): MentionableModel | undefined {
  const models = useModels()
  const modelChoice = useModelChoice()
  const choice = SEARCH_MODEL_CHOICES.find((candidate) => candidate.key === key)
  return modelChoice && choice ? findChoiceModel(models, choice.modelId) : undefined
}

/** Each named choice with its model, which is undefined while the model list hasn't loaded. */
export function useSearchModelChoices(): Array<SearchModelChoice & { model: MentionableModel | undefined }> {
  const models = useModels()
  return SEARCH_MODEL_CHOICES.map((choice) => ({ ...choice, model: findChoiceModel(models, choice.modelId) }))
}
